import { useSearchParams } from "react-router-dom";
import useViewObjectControl from "../../../../../hooks/registers/view/useViewObjectControl";
import { VIEW_TYPES } from "../../../../../config/registers/view/types";
import React, { useCallback } from "react";
import { getNacidRecognition } from "../../../../../axios/api/services";
import { CardContent, CircularProgress } from "@mui/material";
import {
  CardSpg,
  BlockText,
  Page404,
  BoxSpg,
  GridSpg,
  TextSection,
  convertDate,
  parseDate,
  DATE_TIME_FORMAT,
} from "@duosoftbg/nacid-components";
import { useTranslation } from "react-i18next";
import PageWrapper from "../../../../components/common/layout/PageWrapper";
import useViewPageObject from "../../../../../hooks/redux/useViewPageObject";
import { ARNacidStatus } from "../../../../../utils/constants";

const getDecisionProtocolText = (data) => {
  let number = data.lastCommissionCalendarNumber;
  let date = convertToDateString(data.lastCommissionCalendarDate);

  if (!number && !date) {
    return "";
  }

  if (!number) {
    return date;
  }

  return `${number}/${date}`;
};

const convertToDateString = (dateTimeString) => {
  if (!dateTimeString) {
    return null;
  }

  return convertDate(parseDate(dateTimeString, DATE_TIME_FORMAT));
};

const NacidRecognitionView = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const object = useViewPageObject();
  const data = object?.data;
  const viewType = VIEW_TYPES.ACADEMIC_RECOGNITION_NACID;
  const nacidRecognitionId = searchParams.get("id");
  const serviceFn = useCallback(getNacidRecognition(nacidRecognitionId), [nacidRecognitionId]);
  const { isLoading, notFound } = useViewObjectControl({ viewType, serviceFn, viewId: nacidRecognitionId });
  const title = `${t("t.detail.academicRecognition.nacid")} ${
    object ? object.data?.applicationNumber + "/" + object.data?.applicationDate : ""
  }`;

  if (notFound) {
    return <Page404 />;
  }

  return (
    <PageWrapper title={title}>
      <CardSpg my={4} style={{ overflow: "visible" }}>
        <CardContent style={{ padding: 24, position: "relative" }}>
          {isLoading && (
            <BoxSpg my={2} sx={{ display: "flex", boxShadow: 3 }} style={{ justifyContent: "center" }}>
              <CircularProgress size={20} />
            </BoxSpg>
          )}
          {data && (
            <>
              <TextSection label={"l.applicationData"} withDivider>
                <GridSpg container rowSpacing={3}>
                  <BlockText
                    withGrid
                    label={"l.numberAndDate"}
                    text={`${data.applicationNumber}/${data.applicationDate}`}
                  />
                  <BlockText withGrid label={"l.detail.applicantName"} text={data.applicantName} />
                  <BlockText withGrid label={"l.detail.applicationStatus"} text={data.applicationStatus?.name} />
                </GridSpg>
                <AcknowledgedFields data={data} />
                <WeakenedFields data={data} />
                <DeniedFields data={data} />
                <GridSpg container rowSpacing={3} mt={2}>
                  <BlockText withGrid label={"l.detail.foreignUniversity"} text={data.universityName} />
                  <BlockText withGrid label={"l.detail.universityCountry"} text={data.universityCountryName} />
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.trainingInstitution"}
                    text={data.trainingInstitutionName || data.universityName}
                  />
                </GridSpg>
              </TextSection>
              <TextSection label={"l.diplomaData"} withDivider>
                <GridSpg container rowSpacing={3}>
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.detail.originalEduLevel"}
                    text={data.originalEduLevelName}
                  />
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.detail.originalSpeciality"}
                    text={data.originalSpecialityName}
                  />
                  <BlockText
                    withGrid
                    propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
                    label={"l.qualificationFromDiploma"}
                    text={data.originalQualificationName}
                  />
                </GridSpg>
              </TextSection>
              <RecognizedSection data={data} />
            </>
          )}
        </CardContent>
      </CardSpg>
    </PageWrapper>
  );
};

const AcknowledgedFields = ({ data }) => {
  const isAcknowledged = data?.applicationStatus?.id === ARNacidStatus.ACKNOWLEDGED;
  if (!isAcknowledged) {
    return null;
  }

  return (
    <GridSpg container rowSpacing={3} mt={2}>
      <BlockText withGrid label={"l.decisionProtocol"} text={getDecisionProtocolText(data)} />
      <BlockText withGrid label={"l.certificate"} text={data.decisionNumber} />
    </GridSpg>
  );
};

const WeakenedFields = ({ data }) => {
  const isWeakened = data?.applicationStatus?.id === ARNacidStatus.WEAKENED;
  if (!isWeakened) {
    return null;
  }

  return (
    <GridSpg container rowSpacing={3} mt={2}>
      <BlockText withGrid label={"l.decisionProtocol"} text={getDecisionProtocolText(data)} />
      <BlockText withGrid label={"l.certificateAndWeakenedOrder"} text={data.decisionNumber} />
      <BlockText withGrid label={"l.legalReason"} text={data.legalReasonName} />
    </GridSpg>
  );
};

const DeniedFields = ({ data }) => {
  const isDenied = data?.applicationStatus?.id === ARNacidStatus.DENIED;

  if (!isDenied) {
    return null;
  }

  return (
    <GridSpg container rowSpacing={3} mt={2}>
      <BlockText withGrid label={"l.denialDecision"} text={data.decisionNumber} />
      <BlockText withGrid label={"l.legalReason"} text={data.legalReasonName} />
    </GridSpg>
  );
};

const RecognizedSection = ({ data }) => {
  const isAcknowledged = data?.applicationStatus?.id === ARNacidStatus.ACKNOWLEDGED;
  if (!isAcknowledged) {
    return null;
  }

  return (
    <TextSection label={"l.recognized"} withDivider>
      <GridSpg container rowSpacing={3}>
        <BlockText
          withGrid
          propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
          label={"l.detail.recognizedEduLevel"}
          text={data.recognizedEduLevel?.name}
        />
        <BlockText
          withGrid
          propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
          label={"l.recognizedSpeciality"}
          text={data.recognizedSpecialityName}
        />
        <BlockText
          withGrid
          propsGrid={{ xs: 12, md: 12, sm: 12, lg: 12 }}
          label={"l.recognizedQualification"}
          text={data.recognizedQualificationName}
        />
      </GridSpg>
    </TextSection>
  );
};

export default NacidRecognitionView;
